import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Wrench, 
  Zap, 
  Droplet, 
  Hammer, 
  Paintbrush, 
  Home as HomeIcon,
  Phone,
  CheckCircle,
  Building2,
  Mail,
  MapPin,
  Clock,
  Star,
  ArrowRight,
  Menu,
  X,
  User,
  Users,
  LogIn,
  Calendar,
  Grid3x3,
  Layers,
  Trees,
  SquareStack,
  Palette,
  Truck,
  PackageCheck,
  Bath,
  DoorOpen,
  Store,
  KeyRound,
  Building,
  ShieldCheck,
  AlertTriangle,
  ClipboardCheck,
  ChevronDown,
  Image as ImageIcon,
  Sparkles,
  Smartphone,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { HERO_BEFORE, HERO_AFTER } from '../assets/homeImages';
import { MASCOT, TRUCK, EQUIPMENT, TEAM } from '../assets/whyChooseUsImages';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import FeaturedWorkCarousel from '../components/FeaturedWorkCarousel';
import InteractiveMascot from '../components/InteractiveMascot';
import HeroVideo from '../components/HeroVideo';
import PortalMascot from '../components/PortalMascot';
import PromoBanner from '../components/PromoBanner';
import PromoModal from '../components/PromoModal';
import Seo from '../components/Seo';
import { SmsConsentCheckbox } from '../components/SmsConsentCheckbox';
import { ABOUT_FACTS } from './AboutPage';
import GoogleReviews from '../components/GoogleReviews';
import { FALL_SPECIAL, fallPromoCopy } from '../data/fallSpecial';
import { FallOfferBlock, FallServiceGroups } from '../components/FallOfferBlock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { faqJsonLd , servicesItemListJsonLd } from '../data/businessInfo';
import { trackEvent } from '../lib/analytics';
import {
  heroData,
  testimonialsData,
  contactInfo
} from '../data/mock';

const Home = () => {
  const navigate = useNavigate();
  const homeFaqs = [
    { q: 'What areas do you serve?', a: 'We serve the greater Albuquerque area, including Rio Rancho, Corrales, Bernalillo, Placitas, Los Lunas and Santa Fe. Not sure if you\u2019re in range? Just call (505) 219-7816 and ask.' },
    { q: 'What are your hours?', a: 'We\u2019re available 24/7, 365 days a year. Call or text (505) 219-7816 anytime and we\u2019ll get back to you quickly \u2014 including for urgent repairs.' },
    { q: 'Do you offer free estimates?', a: 'Yes \u2014 we provide free, no-obligation estimates on all our services. We\u2019ll assess the job and give you a clear, upfront price before any work begins.' },
    { q: 'What services do you offer?', a: 'We\u2019re a full-service home crew: flooring, stucco, drywall, interior & exterior painting, faux finishes, carpentry, plumbing, electrical, remodeling & renovations, landscaping & xeriscaping, dump-trailer hauling, material delivery and general repairs.' },
    { q: 'What payment methods do you accept?', a: 'We accept major credit and debit cards as well as other common payment methods, and you can conveniently pay your invoice online. We\u2019ll go over the options with your estimate.' },
    { q: 'Are you licensed and insured?', a: 'Yes, A Quality Handyman LLC is fully insured, and we stand behind our workmanship on every job.' },
  ];

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const fallCopy = fallPromoCopy();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [secretClickCount, setSecretClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    sms_consent: false
  });
  const [leadForm, setLeadForm] = useState({ name: '', phone: '', email: '', service: '', sms_consent: false });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(`promo_${FALL_SPECIAL.key}_banner_dismissed`) !== '1') setPromoOpen(true);
    } catch (e) {
      setPromoOpen(true);
    }
  }, []);

  const dismissPromo = () => {
    setPromoOpen(false);
    try { localStorage.setItem(`promo_${FALL_SPECIAL.key}_banner_dismissed`, '1'); } catch (e) { /* ignore */ }
  };
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/contact/inquiry`, {
        name: leadForm.name,
        email: leadForm.email,
        phone: leadForm.phone,
        service: leadForm.service || 'General Inquiry',
        message: 'Free estimate request from homepage quick form.',
        sms_consent: !!leadForm.sms_consent,
        sms_consent_source: 'homepage_quick_estimate'
      });
      toast.success('Thanks! We\u2019ll reach out shortly about your free estimate.');
      setLeadForm({ name: '', phone: '', email: '', service: '', sms_consent: false });
      setLeadSubmitted(true);
      trackEvent('generate_lead', {
        event_category: 'lead',
        event_label: 'homepage_free_estimate_form',
        service: leadForm.service || 'General Inquiry',
      });
    } catch (error) {
      console.error('Error submitting lead form:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setLeadSubmitting(false);
    }
  };

  // Secret admin access - click on "2026" four times
  const handleSecretClick = () => {
    const now = Date.now();
    // Reset if more than 2 seconds between clicks
    if (now - lastClickTime > 2000) {
      setSecretClickCount(1);
    } else {
      setSecretClickCount(prev => prev + 1);
    }
    setLastClickTime(now);

    // After 4 clicks, navigate to hidden admin
    if (secretClickCount >= 3) {
      setSecretClickCount(0);
      navigate('/aqh-mgmt-7816');
    }
  };
  
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact/inquiry`, { ...formData, sms_consent: !!formData.sms_consent, sms_consent_source: 'homepage_contact' });
      
      if (response.data) {
        toast.success('Thank you! We will contact you shortly.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
          sms_consent: false
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Seo
        title="Handyman Albuquerque NM | A Quality Handyman LLC"
        description="Professional handyman services in Albuquerque, NM — home repairs, maintenance, painting, flooring, doors, drywall, stucco & more for residential & commercial properties. Locally owned & operated. Get a fast online estimate or request service. Call (505) 219-7816."
        path="/"
        jsonLd={[faqJsonLd(homeFaqs), servicesItemListJsonLd()]}
      />
      {promoOpen && <PromoBanner onClose={dismissPromo} />}
      <PromoModal />
      {/* Header */}
      <header className={`fixed ${promoOpen ? 'top-10' : 'top-0'} w-full bg-white/95 backdrop-blur-sm shadow-sm z-50 transition-[top] duration-200`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-3">
              <InteractiveMascot />
              <div className="flex flex-col items-start">
                <span className="font-bold text-xl text-slate-900 leading-tight">A Quality Handyman LLC</span>
                <span className="hidden sm:block text-xs text-brand-teal font-semibold leading-tight" data-testid="header-slogan">Expert Solutions for Every Home Project</span>
              </div>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('services')} className="text-slate-600 hover:text-brand-blue transition-colors">
                Services
              </button>

              <Link to="/gallery" className="text-slate-600 hover:text-brand-blue transition-colors" data-testid="nav-gallery-link">
                Gallery
              </Link>

              <Link to="/estimate" className="text-slate-600 hover:text-brand-blue transition-colors" data-testid="nav-estimate-link">
                Instant Estimate
              </Link>

              {/* Commercial Dropdown */}
              <div className="relative group" data-testid="nav-commercial-dropdown">
                <button className="flex items-center gap-1 text-slate-600 hover:text-brand-blue transition-colors" data-testid="nav-commercial-trigger">
                  Commercial <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform" />
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
                  <div className="w-72 bg-white rounded-xl shadow-xl border border-slate-100 py-2">
                    {[
                      { label: 'Commercial Handyman', path: '/commercial-handyman' },
                      { label: 'Commercial Property Maintenance', path: '/commercial-property-maintenance' },
                      { label: 'Facility Maintenance', path: '/facility-maintenance' },
                      { label: 'Rental Property Maintenance', path: '/rental-property-maintenance' },
                      { label: 'Realtor & Property Manager Services', path: '/realtor-services' },
                    ].map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-brand-teal transition-colors"
                        data-testid={`nav-commercial-${item.path.replace('/', '')}`}
                      >
                        {item.label}
                      </Link>
                    ))}
                    <div className="border-t border-slate-100 mt-2 pt-2">
                      <Link
                        to="/commercial"
                        className="block w-full text-left px-4 py-2.5 text-sm font-medium text-brand-teal hover:bg-slate-50 transition-colors"
                        data-testid="nav-commercial-viewall"
                      >
                        View all commercial services →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <button onClick={() => scrollToSection('testimonials')} className="text-slate-600 hover:text-brand-blue transition-colors">
                Testimonials
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-slate-600 hover:text-brand-blue transition-colors" data-testid="nav-faq-link">
                FAQ
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-600 hover:text-brand-blue transition-colors">
                Contact
              </button>
              <Button 
                onClick={() => navigate('/book')}
                className="bg-gradient-to-r from-brand-blue to-brand-teal hover:from-brand-blue-dark hover:to-brand-teal-dark text-white"
              >
                Book Appointment
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-3 space-y-3">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-slate-600 hover:text-brand-blue py-2">
                Services
              </button>

              <Link to="/gallery" onClick={() => setMobileMenuOpen(false)} className="block w-full text-left text-slate-600 hover:text-brand-blue py-2" data-testid="mobile-nav-gallery-link">
                Gallery
              </Link>

              <Link to="/estimate" onClick={() => setMobileMenuOpen(false)} className="block w-full text-left text-slate-600 hover:text-brand-blue py-2" data-testid="mobile-nav-estimate-link">
                Instant Estimate
              </Link>

              <div className="pt-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Commercial</p>
                {[
                  { label: 'Commercial Handyman', path: '/commercial-handyman' },
                  { label: 'Commercial Property Maintenance', path: '/commercial-property-maintenance' },
                  { label: 'Facility Maintenance', path: '/facility-maintenance' },
                  { label: 'Rental Property Maintenance', path: '/rental-property-maintenance' },
                  { label: 'Realtor & Property Manager Services', path: '/realtor-services' },
                ].map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block pl-3 py-2 text-slate-600 hover:text-brand-teal"
                    data-testid={`mobile-nav-commercial-${item.path.replace('/', '')}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              <button onClick={() => scrollToSection('testimonials')} className="block w-full text-left text-slate-600 hover:text-brand-blue py-2">
                Testimonials
              </button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left text-slate-600 hover:text-brand-blue py-2" data-testid="mobile-nav-faq-link">
                FAQ
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-slate-600 hover:text-brand-blue py-2">
                Contact
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className={`relative pb-20 lg:pb-28 overflow-hidden ${promoOpen ? 'pt-32 lg:pt-40' : 'pt-24 lg:pt-32'} transition-[padding] duration-200`}>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-teal-50 to-slate-50 opacity-60"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <Badge className="bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/20">
                Locally Owned & Operated
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight" data-testid="hero-heading">
                Professional Handyman Services in Albuquerque, NM
              </h1>
              <h2 className="text-lg sm:text-xl font-semibold text-brand-teal leading-relaxed" data-testid="hero-slogan">
                Reliable Home Repairs, Maintenance &amp; Installations — Residential &amp; Commercial
              </h2>
              <p className="text-base text-slate-700 font-medium leading-relaxed" data-testid="hero-services-line">
                Reliable home repairs, maintenance, installations, painting, flooring, doors, drywall and more throughout Albuquerque and surrounding areas. Get a fast online estimate or request service today.
              </p>
              <p className="text-sm text-slate-500 leading-relaxed" data-testid="hero-areas-line">
                Serving Albuquerque, Rio Rancho, Corrales, Bernalillo, Los Lunas, Santa Fe &amp; surrounding New Mexico communities.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                <Button 
                  size="lg"
                  onClick={() => scrollToSection('quote')}
                  className="bg-gradient-to-r from-brand-blue to-brand-teal hover:opacity-90 text-white group"
                  data-testid="hero-estimate-btn"
                >
                  <Calendar className="mr-2 w-5 h-5" />
                  Schedule a Free Estimate
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg"
                  onClick={() => navigate('/estimate')}
                  className="bg-orange-500 hover:bg-orange-600 text-white group shadow-md"
                  data-testid="hero-instant-estimate-link"
                >
                  <Sparkles className="mr-2 w-5 h-5" />
                  Get My Estimate Instantly!
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/gallery')}
                  className="border-brand-blue text-brand-blue hover:bg-brand-blue/10"
                  data-testid="hero-gallery-btn"
                >
                  <ImageIcon className="mr-2 w-5 h-5" />
                  View Our Gallery
                </Button>
              </div>
              <div className="flex gap-8 pt-4">
                <div>
                  <div className="text-3xl font-bold text-slate-900">500+</div>
                  <div className="text-sm text-slate-600">Projects Done</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-900">98%</div>
                  <div className="text-sm text-slate-600">Satisfaction</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-900">15+</div>
                  <div className="text-sm text-slate-600">Years Exp</div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col items-center gap-10">
              <HeroVideo />
              <PortalMascot variant="hero" />
              {/* Before/After Exterior Painting Showcase */}
              <div className="relative w-full">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-blue to-brand-teal rounded-2xl opacity-20 blur-2xl"></div>
              <div className="relative grid grid-cols-2 gap-4">
                {/* Before Image */}
                <div className="relative group">
                  <img 
                    src={HERO_BEFORE}
                    alt="Home exterior before painting by A Quality Handyman in Albuquerque, NM"
                    className="rounded-xl shadow-xl w-full h-[240px] lg:h-[300px] object-cover transition-transform group-hover:scale-[1.02]"
                    data-testid="hero-before-image"
                  />
                  <div className="absolute bottom-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                    BEFORE
                  </div>
                </div>
                
                {/* After Image */}
                <div className="relative group">
                  <img 
                    src={HERO_AFTER}
                    alt="Freshly painted home exterior after A Quality Handyman service in Albuquerque, NM"
                    className="rounded-xl shadow-xl w-full h-[240px] lg:h-[300px] object-cover transition-transform group-hover:scale-[1.02]"
                    data-testid="hero-after-image"
                  />
                  <div className="absolute bottom-3 left-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                    AFTER
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seasonal Promo Section — Fall Fix-Up Special */}
      <section className={`relative py-10 sm:py-14 bg-gradient-to-br ${fallCopy.theme.gradient} text-white overflow-hidden`} data-testid="home-fall-ready-promo">
        <div className={`absolute -top-10 -right-10 w-64 h-64 rounded-full ${fallCopy.theme.glow} blur-3xl`} aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-start">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <img src="/mascot-transparent.png" alt="" className="w-12 h-12 sm:w-14 sm:h-14 drop-shadow-lg" loading="lazy" />
                <p className={`text-xs font-bold uppercase tracking-widest ${fallCopy.theme.label}`}>{fallCopy.active ? `Seasonal Special · ${fallCopy.badgeText.replace('Limited time · ', '')}` : 'Seasonal'}</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight" data-testid="home-fall-promo-title">{fallCopy.title} — {fallCopy.tagline}</h2>
              <p className={`${fallCopy.theme.soft} mt-2 max-w-2xl`}>{fallCopy.intro}</p>
              <div className="mt-5 max-w-2xl">
                <FallOfferBlock source="home_fall_section" dark />
              </div>
              <div className="mt-6">
                <FallServiceGroups dark withPhotos />
              </div>
              <button onClick={() => navigate(fallCopy.path)} className={`mt-5 inline-flex items-center text-sm font-semibold ${fallCopy.theme.label} hover:text-white`} data-testid="home-fall-ready-cta">
                See the full special <ArrowRight className="ml-1.5 w-4 h-4" />
              </button>
            </div>
            <button onClick={() => navigate(fallCopy.path)} className="hidden lg:block w-64 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20 hover:scale-[1.02] transition-transform" data-testid="home-fall-flyer">
              <img src={FALL_SPECIAL.flyerWebp} alt={`${fallCopy.title} flyer — A Quality Handyman`} className="w-full h-auto" loading="lazy" />
            </button>
          </div>
        </div>
      </section>

      {/* Quick Lead Form - Request a Free Estimate */}
      <section id="quote" className="py-14 bg-gradient-to-r from-brand-blue to-brand-teal" data-testid="quick-quote-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-2 text-white">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3">Request a Free Estimate</h2>
              <p className="text-white/90 text-base">
                Tell us a little about your project and we'll get right back to you — no obligation, serving the greater Albuquerque area.
              </p>
              <div className="mt-4 flex items-center gap-2 text-white/90">
                <Phone className="w-5 h-5" />
                <a href="tel:+15052197816" className="font-semibold hover:underline">(505) 219-7816</a>
              </div>
            </div>
            <div className="lg:col-span-3">
              {leadSubmitted ? (
                <div className="bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="lead-thankyou">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-teal/10 flex items-center justify-center">
                    <CheckCircle className="w-9 h-9 text-brand-teal" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Thank You! Request Received</h3>
                  <p className="text-slate-600 mb-6">
                    We've got your details and a member of our team will reach out shortly to schedule your free estimate.
                  </p>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
                    <p className="text-sm text-slate-600 mb-2">Have an urgent job? Call us now — we're available 24/7:</p>
                    <a href="tel:+15052197816" className="inline-flex items-center gap-2 text-xl font-bold text-brand-blue hover:underline" data-testid="thankyou-call-link">
                      <Phone className="w-5 h-5" /> (505) 219-7816
                    </a>
                  </div>
                  <button
                    onClick={() => setLeadSubmitted(false)}
                    className="text-sm font-medium text-brand-teal hover:underline"
                    data-testid="lead-submit-another"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
              <form onSubmit={handleLeadSubmit} className="bg-white rounded-2xl shadow-xl p-6 grid sm:grid-cols-2 gap-4" data-testid="quick-quote-form">
                <Input
                  type="text"
                  required
                  placeholder="Your Name*"
                  value={leadForm.name}
                  onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                  data-testid="lead-name-input"
                />
                <Input
                  type="tel"
                  required
                  placeholder="Phone*"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                  data-testid="lead-phone-input"
                />
                <Input
                  type="email"
                  required
                  placeholder="Email*"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                  data-testid="lead-email-input"
                />
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-teal"
                  value={leadForm.service}
                  onChange={(e) => setLeadForm({ ...leadForm, service: e.target.value })}
                  data-testid="lead-service-select"
                >
                  <option value="">Select a service…</option>
                  <option>Flooring</option>
                  <option>Stucco</option>
                  <option>Drywall</option>
                  <option>Interior Painting</option>
                  <option>Exterior Painting</option>
                  <option>Remodeling & Renovations</option>
                  <option>Carpentry</option>
                  <option>Plumbing</option>
                  <option>Electrical</option>
                  <option>Landscaping & Xeriscaping</option>
                  <option>Hauling & Material Delivery</option>
                  <option>General Repairs</option>
                  <option>Other</option>
                </select>
                <SmsConsentCheckbox
                  id="quick-lead-sms-consent"
                  testId="quick-lead-sms-consent"
                  className="sm:col-span-2"
                  checked={leadForm.sms_consent}
                  onCheckedChange={(v) => setLeadForm({ ...leadForm, sms_consent: v })}
                />
                <Button
                  type="submit"
                  disabled={leadSubmitting}
                  className="sm:col-span-2 h-11 bg-gradient-to-r from-brand-blue to-brand-teal hover:opacity-90 text-white font-semibold"
                  data-testid="lead-submit-btn"
                >
                  {leadSubmitting ? 'Sending…' : 'Schedule a Free Estimate'}
                </Button>
                <p className="sm:col-span-2 flex items-center justify-center gap-1.5 text-xs text-brand-teal font-medium" data-testid="quote-response-time">
                  <Clock className="w-3.5 h-3.5" /> We typically respond within 1 business day.
                </p>
              </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Work Carousel */}
      <FeaturedWorkCarousel />

      {/* Services Section - Service Portfolio Links */}
      <section id="services" className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-brand-teal/10 text-brand-teal hover:bg-emerald-100 mb-4">
              Our Services
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Complete Home Services in the Greater Albuquerque Area
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From flooring and stucco to remodeling, landscaping and repairs, we're your one trusted local crew serving Albuquerque, Rio Rancho, Santa Fe & surrounding communities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* General Repairs */}
            <Link to="/general-repairs" className="block">
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              <div className="h-48 bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                <Wrench className="w-20 h-20 text-white/80 group-hover:scale-110 transition-transform" />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">General Repairs</h3>
                <p className="text-slate-600 text-sm mb-4">Drywall, doors, windows, hardware installation & more</p>
                <Button variant="outline" className="w-full group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  View Portfolio & Book
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            </Link>

            {/* Plumbing */}
            <Link to="/plumbing" className="block">
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              <div className="h-48 bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                <Droplet className="w-20 h-20 text-white/80 group-hover:scale-110 transition-transform" />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Plumbing</h3>
                <p className="text-slate-600 text-sm mb-4">Leak repairs, pipe installation, fixtures & drain cleaning</p>
                <Button variant="outline" className="w-full group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  View Portfolio & Book
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            </Link>

            {/* Electrical */}
            <Link to="/electrical" className="block">
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              <div className="h-48 bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
                <Zap className="w-20 h-20 text-white/80 group-hover:scale-110 transition-transform" />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Electrical</h3>
                <p className="text-slate-600 text-sm mb-4">Outlets, lighting, panel upgrades & safety inspections</p>
                <Button variant="outline" className="w-full group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  View Portfolio & Book
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            </Link>

            {/* Carpentry */}
            <Link to="/carpentry" className="block">
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              <div className="h-48 bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center">
                <Hammer className="w-20 h-20 text-white/80 group-hover:scale-110 transition-transform" />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Carpentry</h3>
                <p className="text-slate-600 text-sm mb-4">Custom cabinets, trim work, decks & furniture repair</p>
                <Button variant="outline" className="w-full group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  View Portfolio & Book
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            </Link>

            {/* Building Maintenance */}
            <Link to="/building-maintenance" className="block">
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              <div className="h-48 bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center">
                <HomeIcon className="w-20 h-20 text-white/80 group-hover:scale-110 transition-transform" />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Building Maintenance</h3>
                <p className="text-slate-600 text-sm mb-4">Commercial, residential, seasonal & cleaning services</p>
                <Button variant="outline" className="w-full group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  View Portfolio & Book
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            </Link>

            {/* Exterior Painting */}
            <Link to="/exterior-painting" className="block">
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              <div className="h-48 bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
                <Paintbrush className="w-20 h-20 text-white/80 group-hover:scale-110 transition-transform" />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Exterior Painting</h3>
                <p className="text-slate-600 text-sm mb-4">Commercial & residential exterior painting</p>
                <Button variant="outline" className="w-full group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  View Portfolio & Book
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            </Link>

            {/* Flooring */}
            <Link to="/flooring" className="block">
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              data-testid="service-card-flooring"
            >
              <div className="h-48 bg-gradient-to-br from-stone-400 to-stone-600 flex items-center justify-center">
                <Grid3x3 className="w-20 h-20 text-white/80 group-hover:scale-110 transition-transform" />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Flooring</h3>
                <p className="text-slate-600 text-sm mb-4">Tile, carpet, LVP, laminate & hardwood installation</p>
                <Button variant="outline" className="w-full group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  View Portfolio & Book
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            </Link>

            {/* Stucco */}
            <Link to="/stucco" className="block">
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              data-testid="service-card-stucco"
            >
              <div className="h-48 bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center">
                <Layers className="w-20 h-20 text-white/80 group-hover:scale-110 transition-transform" />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Stucco</h3>
                <p className="text-slate-600 text-sm mb-4">New stucco, repair, re-stucco & custom textures</p>
                <Button variant="outline" className="w-full group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  View Portfolio & Book
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            </Link>

            {/* Remodeling & Renovations */}
            <Link to="/remodeling" className="block">
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              data-testid="service-card-remodeling"
            >
              <div className="h-48 bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
                <HomeIcon className="w-20 h-20 text-white/80 group-hover:scale-110 transition-transform" />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Remodeling & Renovations</h3>
                <p className="text-slate-600 text-sm mb-4">Kitchens, baths, additions & whole-home makeovers</p>
                <Button variant="outline" className="w-full group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  View Portfolio & Book
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            </Link>

            {/* Landscaping & Xeriscaping */}
            <Link to="/landscaping" className="block">
            <Card 
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              data-testid="service-card-landscaping"
            >
              <div className="h-48 bg-gradient-to-br from-green-500 to-emerald-700 flex items-center justify-center">
                <Trees className="w-20 h-20 text-white/80 group-hover:scale-110 transition-transform" />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Landscaping & Xeriscaping</h3>
                <p className="text-slate-600 text-sm mb-4">Desert-smart yards, gravel, pavers & drip irrigation</p>
                <Button variant="outline" className="w-full group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  View Portfolio & Book
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            </Link>
          </div>

          {/* Full Service List - Albuquerque SEO */}
          <div className="mt-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Every Home Service You Need — One Local Call</h3>
              <p className="text-slate-600 max-w-3xl mx-auto">
                A Quality Handyman LLC proudly serves homeowners and businesses across the greater Albuquerque area, including Rio Rancho, Santa Fe, Bernalillo, Corrales and Los Lunas. Explore our full range of services below.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3" data-testid="service-chips">
              {[
                { label: 'Tile Flooring', path: '/flooring', icon: Grid3x3 },
                { label: 'Carpet', path: '/flooring', icon: Grid3x3 },
                { label: 'LVP & Laminate', path: '/flooring', icon: Grid3x3 },
                { label: 'Hardwood Floors', path: '/flooring', icon: Grid3x3 },
                { label: 'Stucco', path: '/stucco', icon: Layers },
                { label: 'Drywall', path: '/drywall', icon: SquareStack },
                { label: 'Faux Finishes', path: '/faux-finishes', icon: Palette },
                { label: 'Interior Painting', path: '/interior-painting', icon: Paintbrush },
                { label: 'Exterior Painting', path: '/exterior-painting', icon: Paintbrush },
                { label: 'Remodeling', path: '/remodeling', icon: HomeIcon },
                { label: 'Renovations', path: '/remodeling', icon: HomeIcon },
                { label: 'Carpentry', path: '/carpentry', icon: Hammer },
                { label: 'Plumbing', path: '/plumbing', icon: Droplet },
                { label: 'Electrical', path: '/electrical', icon: Zap },
                { label: 'General Repairs', path: '/general-repairs', icon: Wrench },
                { label: 'Landscaping', path: '/landscaping', icon: Trees },
                { label: 'Xeriscaping', path: '/landscaping', icon: Trees },
                { label: 'Dump Trailer & Hauling', path: '/hauling', icon: Truck },
                { label: 'Material Delivery', path: '/material-delivery', icon: PackageCheck },
                { label: 'Building Maintenance', path: '/building-maintenance', icon: HomeIcon },
                { label: 'Bathroom Remodeling', path: '/bathroom-remodeling', icon: Bath },
                { label: 'Door Replacement', path: '/door-replacement', icon: DoorOpen },
                { label: 'Facility Maintenance', path: '/facility-maintenance', icon: Wrench },
                { label: 'Rental Property Maintenance', path: '/rental-property-maintenance', icon: KeyRound },
                { label: 'Residential Handyman', path: '/residential-handyman', icon: HomeIcon },
                { label: 'Commercial Handyman', path: '/commercial-handyman', icon: Building2 },
                { label: 'Commercial Property Maintenance', path: '/commercial-property-maintenance', icon: Store },
              ].map((chip) => {
                const ChipIcon = chip.icon;
                return (
                  <Link
                    key={chip.label}
                    to={chip.path}
                    data-testid={`service-chip-${chip.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-medium shadow-sm hover:border-brand-teal hover:text-brand-teal hover:shadow-md hover:-translate-y-0.5 transition-all"
                  >
                    <ChipIcon className="w-4 h-4 text-brand-teal group-hover:scale-110 transition-transform" />
                    {chip.label}
                  </Link>
                );
              })}
            </div>

            {/* Popular Local Services - internal links for local SEO */}
            <div className="mt-10 text-center">
              <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Popular Local Services</p>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 max-w-4xl mx-auto" data-testid="local-service-links">
                {[
                  { label: 'Drywall in Rio Rancho', slug: 'drywall-rio-rancho' },
                  { label: 'Interior Painting in Rio Rancho', slug: 'interior-painting-rio-rancho' },
                  { label: 'Stucco in Santa Fe', slug: 'stucco-santa-fe' },
                  { label: 'Flooring in Rio Rancho', slug: 'flooring-rio-rancho' },
                  { label: 'Commercial Painting in Albuquerque', slug: 'commercial-painting-albuquerque' },
                  { label: 'Plumbing in Rio Rancho', slug: 'plumbing-rio-rancho' },
                  { label: 'Remodeling in Santa Fe', slug: 'remodeling-santa-fe' },
                  { label: 'Drywall in Santa Fe', slug: 'drywall-santa-fe' },
                ].map((l) => (
                  <Link
                    key={l.slug}
                    to={`/services/${l.slug}`}
                    className="text-sm text-slate-600 hover:text-brand-teal hover:underline transition-colors"
                    data-testid={`local-link-${l.slug}`}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Services - Albuquerque Commercial SEO */}
      <section id="commercial" className="py-20 bg-slate-900 text-white" data-testid="commercial-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="bg-brand-teal/20 text-brand-teal hover:bg-brand-teal/30 mb-4">
              <Building2 className="w-4 h-4 mr-2" /> Commercial Services
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Commercial Handyman & Facility Maintenance in Albuquerque
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              One trusted, fully-insured vendor for every commercial property in the greater Albuquerque area — retail, restaurants, offices, apartments and multi-tenant buildings. Scheduled upkeep, fast repairs and emergency response, all on one invoice.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { title: 'Commercial Handyman Albuquerque', desc: 'All-trades repairs and upkeep for any business.', path: '/commercial-handyman', icon: Building2 },
              { title: 'Facility Maintenance Albuquerque', desc: 'Preventive plans and total building care.', path: '/facility-maintenance', icon: Wrench },
              { title: 'Commercial Building Maintenance Albuquerque', desc: 'Ongoing maintenance for offices and buildings.', path: '/commercial-property-maintenance', icon: Building },
              { title: 'Retail Maintenance Albuquerque', desc: 'Keep storefronts clean, safe and customer-ready.', path: '/commercial-property-maintenance', icon: Store },
              { title: 'Restaurant Maintenance Albuquerque', desc: 'Discreet, fast repairs that keep you serving.', path: '/commercial-property-maintenance', icon: ClipboardCheck },
              { title: 'Property Management Maintenance Albuquerque', desc: 'A reliable single vendor for your portfolio.', path: '/rental-property-maintenance', icon: KeyRound },
              { title: 'Apartment Maintenance Albuquerque', desc: 'Unit turnovers, repairs and common-area upkeep.', path: '/rental-property-maintenance', icon: ShieldCheck },
              { title: 'Emergency Commercial Repairs Albuquerque', desc: 'Rapid response when downtime isn’t an option.', path: '/commercial-handyman', icon: AlertTriangle },
              { title: 'Realtor & Property Manager Services Albuquerque', desc: 'Fast turnovers, make-readies and punch lists.', path: '/realtor-services', icon: KeyRound },
            ].map((c) => {
              const CIcon = c.icon;
              return (
                <Link
                  key={c.title}
                  to={c.path}
                  data-testid={`commercial-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="group flex flex-col gap-3 p-6 rounded-2xl bg-slate-800/60 border border-slate-700 hover:border-brand-teal hover:bg-slate-800 hover:-translate-y-1 transition-all"
                >
                  <div className="w-11 h-11 rounded-xl bg-brand-teal/15 flex items-center justify-center">
                    <CIcon className="w-6 h-6 text-brand-teal" />
                  </div>
                  <h3 className="font-semibold text-white leading-snug group-hover:text-brand-teal transition-colors">{c.title}</h3>
                  <p className="text-sm text-slate-400">{c.desc}</p>
                  <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-brand-teal">
                    Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Button
              size="lg"
              onClick={() => navigate('/commercial')}
              className="bg-brand-teal hover:bg-brand-teal/90 text-white"
              data-testid="commercial-cta-btn"
            >
              <ArrowRight className="w-5 h-5 mr-2" />
              Explore All Commercial Services
            </Button>
          </div>
        </div>
      </section>

      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-brand-blue/10 text-brand-blue hover:bg-teal-100 mb-4">
              Testimonials
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Don't just take our word for it. Here's what our satisfied customers have to say.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonialsData.map((testimonial) => (
              <Card 
                key={testimonial.id}
                className="border-slate-200 hover:shadow-lg transition-shadow"
              >
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12 bg-gradient-to-br from-brand-blue to-brand-teal">
                      <AvatarFallback className="text-white font-semibold">
                        {testimonial.avatar}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-semibold text-slate-900">{testimonial.name}</div>
                      <div className="text-sm text-slate-600">{testimonial.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Google Business Reviews (hidden until configured) */}
      <GoogleReviews />

      {/* Why Choose Us / Our Fleet Section */}
      <section className="py-20 bg-gradient-to-br from-brand-blue/5 to-brand-teal/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-brand-teal/10 text-brand-teal hover:bg-teal-100 mb-4">
              Why Choose Us
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Equipped to Handle Any Job
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Professional equipment, experienced team, and a commitment to quality that shows in everything we do.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 items-center">
            {/* Mascot with branding */}
            <div className="text-center">
              <img 
                src={MASCOT}
                alt="A Quality Handyman - Your Trusted Home Service Experts"
                className="w-64 h-64 mx-auto object-contain"
                data-testid="footer-mascot-image"
              />
              <h3 className="text-xl font-bold text-slate-900 mt-4">Your Trusted Partner</h3>
              <p className="text-slate-600">Fully insured professionals ready to serve</p>
            </div>

            {/* Fleet Photos */}
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
              <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img loading="lazy" decoding="async" 
                  src={TRUCK}
                  alt="A Quality Handyman service truck in Albuquerque, NM"
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-4">
                  <h4 className="font-semibold text-slate-900">Fully Equipped Service Vehicles</h4>
                  <p className="text-sm text-slate-600">Ready to handle any residential project</p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img loading="lazy" decoding="async" 
                  src={EQUIPMENT}
                  alt="A Quality Handyman equipment for home repair and maintenance in Albuquerque, NM"
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-4">
                  <h4 className="font-semibold text-slate-900">Heavy-Duty Equipment</h4>
                  <p className="text-sm text-slate-600">Dually truck & trailer for bigger jobs</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      {/* About / company facts (same text as /about and the static summary in index.html) */}
      <section id="about" className="py-16 bg-white" data-testid="home-about-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">About A Quality Handyman LLC</h2>
          <div className="grid md:grid-cols-2 gap-6 text-slate-600 leading-relaxed">
            <p>{ABOUT_FACTS.what}</p>
            <p>{ABOUT_FACTS.who}</p>
            <p>{ABOUT_FACTS.where}</p>
            <p>{ABOUT_FACTS.how}</p>
          </div>
          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            <Link to="/about" className="text-brand-blue font-semibold underline underline-offset-2" data-testid="home-about-link">More about us →</Link>
            <a href="tel:+15052197816" className="text-brand-blue font-semibold underline underline-offset-2">(505) 219-7816</a>
            <a href="mailto:admin@aqualityhandyman.com" className="text-brand-blue font-semibold underline underline-offset-2">admin@aqualityhandyman.com</a>
          </div>
        </div>
      </section>

      <section id="contact" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 mb-4">
              Get In Touch
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Contact us today for a free quote. We're here to help with all your home improvement needs.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="shadow-xl border-slate-200">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <Input 
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                      className="border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <Input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      required
                      className="border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Phone *
                    </label>
                    <Input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(505) 219-7816"
                      required
                      className="border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Service Needed *
                    </label>
                    <Input 
                      type="text"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      placeholder="e.g., Plumbing repair"
                      required
                      className="border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Project Details
                    </label>
                    <Textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your project..."
                      rows={4}
                      className="border-slate-300"
                    />
                  </div>
                  <SmsConsentCheckbox
                    id="contact-sms-consent"
                    testId="contact-sms-consent"
                    checked={formData.sms_consent}
                    onCheckedChange={(v) => setFormData({ ...formData, sms_consent: v })}
                  />
                  <Button 
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-brand-blue to-brand-teal hover:from-brand-blue-dark hover:to-brand-teal-dark text-white"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              {/* Office Staff Photo */}
              <Card className="shadow-lg border-slate-200 overflow-hidden">
                <img loading="lazy" decoding="async" 
                  src={TEAM}
                  alt="A Quality Handyman professional team serving Albuquerque, NM"
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-900 text-center">Our Professional Team</h3>
                  <p className="text-sm text-slate-600 text-center">Dedicated experts ready to serve you</p>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-brand-blue to-brand-teal rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                      <p className="text-slate-600">{contactInfo.phone}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-brand-blue to-brand-teal rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                      <p className="text-slate-600">{contactInfo.email}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-brand-blue to-brand-teal rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Address</h3>
                      <p className="text-slate-600">{contactInfo.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-brand-blue to-brand-teal rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Business Hours</h3>
                      <p className="text-slate-600">{contactInfo.hours}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* General FAQ */}
      <section id="faq" className="py-20 bg-slate-50" data-testid="home-faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-slate-600">
              Quick answers about working with A Quality Handyman LLC in the greater Albuquerque area.
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {homeFaqs.map((faq, index) => (
              <AccordionItem key={index} value={`home-faq-${index}`} data-testid={`home-faq-item-${index}`}>
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>


      {/* Get the app */}
      <section className="bg-gradient-to-r from-brand-blue to-brand-teal text-white" data-testid="home-get-app-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row items-center gap-6 md:gap-10">
          <img src="/icon-192.png" alt="AQH app icon" className="w-20 h-20 rounded-3xl shadow-xl shrink-0" loading="lazy" />
          <div className="flex-1 text-center md:text-left">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">For our crew & office</p>
            <h2 className="text-2xl md:text-3xl font-bold">Get the A Quality Handyman app</h2>
            <p className="text-white/85 mt-1 text-sm md:text-base">Free, no app store needed. Installs from this website in under a minute and opens full-screen on your phone — jobs, photos, mileage and texts, even without signal.</p>
          </div>
          <Button onClick={() => navigate('/app')} size="lg" className="bg-white text-slate-900 hover:bg-slate-100 shadow-lg shrink-0" data-testid="home-get-app-btn">
            <Smartphone className="w-5 h-5 mr-2" />Download the app
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <div className="flex flex-col items-start">
                  <span className="font-bold text-2xl text-white leading-tight">A Quality Handyman LLC</span>
                </div>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                Your trusted partner for all home improvement and repair needs in Albuquerque, Rio Rancho & Santa Fe.
              </p>
              {/* Social Media Links */}
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.facebook.com/share/1C5QubX6nN/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-slate-800 hover:bg-blue-600 flex items-center justify-center transition-colors"
                  title="Follow us on Facebook"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a 
                  href="#" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-slate-800 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500 flex items-center justify-center transition-all opacity-50"
                  title="Instagram - Coming Soon"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a 
                  href="https://nextdoor.com/pages/a-quality-handy-man-service-albuquerque-nm/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-slate-800 hover:bg-green-600 flex items-center justify-center transition-colors"
                  title="Find us on Nextdoor"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
                </a>
                <a 
                  href="https://maps.app.goo.gl/UGNFuEhupLGgjFpE8" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-slate-800 hover:bg-red-500 flex items-center justify-center transition-colors"
                  title="Find us on Google Maps"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/flooring" className="hover:text-brand-blue-light transition-colors">Flooring</Link></li>
                <li><Link to="/stucco" className="hover:text-brand-blue-light transition-colors">Stucco</Link></li>
                <li><Link to="/remodeling" className="hover:text-brand-blue-light transition-colors">Remodeling</Link></li>
                <li><Link to="/landscaping" className="hover:text-brand-blue-light transition-colors">Landscaping & Xeriscaping</Link></li>
                <li><Link to="/interior-painting" className="hover:text-brand-blue-light transition-colors">Interior Painting</Link></li>
                <li><Link to="/carpentry" className="hover:text-brand-blue-light transition-colors">Carpentry</Link></li>
                <li><Link to="/plumbing" className="hover:text-brand-blue-light transition-colors">Plumbing</Link></li>
                <li><Link to="/general-repairs" className="hover:text-brand-blue-light transition-colors">General Repairs</Link></li>
                <li><Link to="/exterior-painting" className="hover:text-brand-blue-light transition-colors">Exterior Painting</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><button onClick={() => scrollToSection('services')} className="hover:text-brand-blue-light transition-colors">All Services</button></li>
                <li><Link to="/blog" className="hover:text-brand-blue-light transition-colors">Blog & Tips</Link></li>
                <li><button onClick={() => scrollToSection('testimonials')} className="hover:text-brand-blue-light transition-colors">Testimonials</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-brand-blue-light transition-colors">FAQ</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-brand-blue-light transition-colors">Contact Us</button></li>
                <li><Link to="/rio-rancho" className="hover:text-brand-blue-light transition-colors">Handyman in Rio Rancho</Link></li>
                <li><Link to="/santa-fe" className="hover:text-brand-blue-light transition-colors">Handyman in Santa Fe</Link></li>
                <li><Link to="/corrales" className="hover:text-brand-blue-light transition-colors">Handyman in Corrales</Link></li>
                <li><Link to="/bernalillo" className="hover:text-brand-blue-light transition-colors">Handyman in Bernalillo</Link></li>
                <li><Link to="/los-lunas" className="hover:text-brand-blue-light transition-colors">Handyman in Los Lunas</Link></li>
                <li><Link to="/placitas" className="hover:text-brand-blue-light transition-colors">Handyman in Placitas</Link></li>
                <li><a href="https://maps.app.goo.gl/UGNFuEhupLGgjFpE8" target="_blank" rel="noopener noreferrer" className="hover:text-brand-blue-light transition-colors">Find Us on Google</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-brand-teal" />{contactInfo.phone}</li>
                <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-brand-teal" />{contactInfo.email}</li>
                <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-brand-teal" />{contactInfo.hours}</li>
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-teal" />Albuquerque, NM</li>
              </ul>
              {/* Google Business CTA */}
              <a 
                href="https://maps.app.goo.gl/UGNFuEhupLGgjFpE8" 
                target="_blank" 
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-800 rounded-lg text-sm font-medium hover:bg-slate-100 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/></svg>
                Find Us on Google
              </a>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-slate-400">
            <p>&copy; <span onClick={handleSecretClick} className="cursor-default select-none">2026</span> {heroData.businessName} - {heroData.businessSubtitle}. All rights reserved.</p>
            <div className="flex items-center gap-4 text-sm">
              <span className="hidden sm:inline text-slate-600">|</span>
              <button 
                onClick={() => navigate('/customer/login')} 
                className="group flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-transparent hover:border-brand-blue-light/30 hover:bg-brand-blue-light/10 hover:text-brand-blue-light transition-all duration-300"
                data-testid="footer-customer-login"
              >
                <User className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                <span className="group-hover:translate-x-0.5 transition-transform">Customer Login</span>
              </button>
              <button 
                onClick={() => navigate('/employee/login')} 
                className="group flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-transparent hover:border-brand-teal/30 hover:bg-brand-teal/10 hover:text-brand-teal transition-all duration-300"
                data-testid="footer-employee-login"
              >
                <Users className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                <span className="group-hover:translate-x-0.5 transition-transform">Employee Login</span>
              </button>
            </div>
          </div>
          {/* Legal Links */}
          <div className="border-t border-slate-800 mt-6 pt-4 flex flex-wrap justify-center gap-4 text-xs text-slate-500">
            <button onClick={() => navigate('/terms')} className="hover:text-slate-300 transition-colors">Terms of Service</button>
            <span>|</span>
            <button onClick={() => navigate('/privacy')} className="hover:text-slate-300 transition-colors">Privacy Policy</button>
            <span>|</span>
            <button onClick={() => navigate('/cookies')} className="hover:text-slate-300 transition-colors">Cookie Policy</button>
            <span>|</span>
            <button onClick={() => navigate('/sms-terms')} className="hover:text-slate-300 transition-colors" data-testid="footer-sms-terms">SMS Terms</button>
            <span>|</span>
            <button onClick={() => navigate('/about')} className="hover:text-slate-300 transition-colors" data-testid="footer-about">About Us</button>
            <span>|</span>
            <button onClick={() => navigate('/media-kit')} className="hover:text-slate-300 transition-colors" data-testid="footer-media-kit">Media Kit</button>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Portal Access</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-center text-slate-600 text-sm">
              Choose your portal to login
            </p>
            
            <Button
              onClick={() => {
                setShowLoginModal(false);
                navigate('/customer/login');
              }}
              className="w-full h-14 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
              data-testid="customer-portal-btn"
            >
              <User className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div className="font-semibold">Customer Portal</div>
                <div className="text-xs opacity-80">Track jobs & view invoices</div>
              </div>
            </Button>

            <Button
              onClick={() => {
                setShowLoginModal(false);
                navigate('/employee/login');
              }}
              className="w-full h-14 bg-gradient-to-r from-brand-blue to-brand-teal hover:from-brand-blue-dark hover:to-brand-teal-dark text-white"
              data-testid="employee-portal-btn"
            >
              <Users className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div className="font-semibold">Employee Portal</div>
                <div className="text-xs opacity-80">Manage jobs & track time</div>
              </div>
            </Button>

            <div className="pt-2 border-t">
              <Button
                onClick={() => {
                  setShowLoginModal(false);
                  navigate('/admin');
                }}
                variant="outline"
                className="w-full"
                data-testid="admin-dashboard-btn"
              >
                Admin Dashboard
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;

